<script setup lang="ts">
    import { computed } from 'vue';

    type ToggleOption = { label: string; value: string; disabled?: boolean };

    const props = withDefaults(
        defineProps<{
            modelValue?: string;
            options: ToggleOption[];
            mandatory?: boolean;
            errorMessages?: string;
            disabled?: boolean;
        }>(),
        {
            mandatory: true,
            modelValue: '',
        },
    );

    const buttonWidth = computed(() => {
        return `${100 / props.options.length}%`;
    });
</script>

<template>
    <div class="app-toggle-container">
        <v-btn-toggle
            class="app-btn-toggle w-100"
            :class="errorMessages?.length ? 'has-error' : ''"
            :mandatory="mandatory"
            :model-value="modelValue"
            :disabled="disabled"
            @update:model-value="$emit('update:modelValue', $event)"
        >
            <app-button
                v-for="option in options"
                :key="option.value"
                role="radio"
                :value="option.value"
                :width="buttonWidth"
                height="32"
                color="secondary-text"
                :disabled="option.disabled"
            >
                {{ option.label }}
            </app-button>
        </v-btn-toggle>

        <div v-if="errorMessages" class="v-input__details w-100">
            <div class="v-messages opacity-100">
                <div class="v-messages__message text-error" data-testid="error-message">{{ errorMessages }}</div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .app-toggle-container {
        width: 100%;
    }

    .app-btn-toggle {
        height: 40px;
        background-color: rgb(var(--v-theme-on-increment-grey));
        padding: 4px;
        border-radius: 28px;
        display: block;
    }

    .has-error.v-btn-group {
        border: 1px solid rgb(var(--v-theme-error)) !important;
    }

    :deep(.v-btn__content) {
        color: rgb(var(--v-theme-secondary-text)) !important;
        font-weight: 500 !important;
    }

    :deep(.v-btn:not(.v-btn--active)) {
        background-color: rgb(var(--v-theme-on-increment-grey));
    }

    :deep(.v-btn.v-btn--active) {
        background-color: rgb(var(--v-theme-surface)) !important;
        border: none !important;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.1) !important;
    }

    :deep(.v-btn.v-btn--active:disabled) {
        background-color: rgb(var(--v-theme-background)) !important;
    }

    :deep(.v-btn__overlay) {
        display: none;
    }

    .v-input__details {
        padding-inline: 16px;
    }
</style>
